import { useState } from "react";
import { Check, Loader2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const STEPS = [["a_caminho", "A caminho"], ["proximo", "Chegando"], ["chegou", "Chegou"]];
const ORDER = ["confirmado", "a_caminho", "proximo", "chegou"];

export function TrackSteps({ status }) {
  const cur = ORDER.indexOf(status);
  return (
    <ol className="flex items-start justify-between" aria-label="Etapas do deslocamento" data-testid="track-steps">
      {STEPS.map(([k, l], i) => {
        const idx = ORDER.indexOf(k), done = cur >= idx, active = cur === idx;
        return (
          <li key={k} className="flex-1 flex flex-col items-center gap-1 relative" aria-current={active ? "step" : undefined}>
            {i > 0 && <span aria-hidden="true" className={`absolute top-2.5 right-1/2 w-full h-0.5 ${done ? "bg-[#FF4D00]" : "bg-white/10"}`} />}
            <span aria-hidden="true" className={`relative z-10 h-5 w-5 rounded-full border-2 flex items-center justify-center ${done ? "bg-[#FF4D00] border-[#FF4D00]" : "bg-[#121217] border-white/25"}`}>
              {done && <Check className="h-3 w-3 text-white" />}
            </span>
            <span className={`text-xs ${done ? "text-white font-semibold" : "text-zinc-400"}`}>{l}<span className="sr-only">{done ? " (concluído)" : ""}</span></span>
          </li>
        );
      })}
    </ol>
  );
}

export function PersonRow({ p, right }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-12 w-12 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold shrink-0 border-2 border-[#FF4D00]/60">
        {p.photo ? <img src={p.photo} alt="" className="h-full w-full object-cover" /> : (p.stage_name?.[0] || "?")}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{p.stage_name || "Músico"}</div>
        <div className="text-xs text-zinc-300 flex items-center gap-2 flex-wrap">
          {p.rating > 0 && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400 fill-amber-400" aria-hidden="true" /><span className="sr-only">Avaliação</span>{p.rating}{p.reviews ? ` (${p.reviews})` : ""}</span>}
          {p.instrument && <span>{p.instrument}</span>}
          {p.gigs != null && <span>{p.gigs} show{p.gigs === 1 ? "" : "s"}</span>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function EtaBadge({ eta, km, source, stale }) {
  const off = eta == null;
  return (
    <div className="text-right shrink-0" data-testid="track-eta">
      <div className={`font-head text-lg font-extrabold ${off ? "text-zinc-400" : stale ? "text-amber-300" : "text-emerald-400"}`}>{off ? "—" : `${eta} min`}</div>
      <div className="text-xs text-zinc-400">{km != null ? `${km} km` : "distância indisponível"}</div>
      {source === "estimate" && <div className="text-[11px] text-amber-300">estimativa (sem rota)</div>}
      {source === "unavailable" && <div className="text-[11px] text-zinc-400">tempo indisponível</div>}
    </div>
  );
}

export function CancelDialog({ open, onOpenChange, title, policy = [], onConfirm, showSubstitute }) {
  const [reason, setReason] = useState("");
  const [sub, setSub] = useState(true);
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 3;
  const go = async () => { setBusy(true); try { await onConfirm(reason.trim(), sub); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121217] border-white/10 rounded-2xl" data-testid="cancel-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Informe o motivo. Confira a política antes de confirmar.</DialogDescription></DialogHeader>
        <ul className="text-xs text-zinc-300 space-y-1 list-disc pl-4" data-testid="cancel-policy">{policy.map((p) => <li key={p}>{p}</li>)}</ul>
        <label htmlFor="cancel-reason" className="text-sm text-zinc-200">Motivo do cancelamento</label>
        <textarea id="cancel-reason" data-testid="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="w-full rounded-xl bg-[#09090B] border border-white/15 p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]" />
        {showSubstitute && (
          <label className="flex items-center gap-2 text-sm min-h-[44px]"><input type="checkbox" checked={sub} onChange={(e) => setSub(e.target.checked)} data-testid="cancel-substitute" className="h-5 w-5 accent-[#FF4D00]" />Buscar substituto automaticamente</label>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 rounded-xl bg-transparent border-white/15">Voltar</Button>
          <Button data-testid="cancel-confirm" disabled={!ok || busy} onClick={go} className="h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />}Confirmar cancelamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const mapsLink = (c, addr) => c?.lat != null ? `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || "")}`;
export const wazeLink = (c) => c?.lat != null ? `https://waze.com/ul?ll=${c.lat},${c.lng}&navigate=yes` : null;
